import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';
import { CodeBlock } from '../converter-layout/code-block';

type Format = 'woff2' | 'woff' | 'truetype' | 'opentype' | 'svg' | 'embedded-opentype';

interface FontSource {
    url: string;
    format: Format;
}

interface State {
    family: string;
    sources: FontSource[];
    weight: string;
    style: string;
    display: string;
    unicodeRange: string;
    copied: boolean;
}

export default class FontFaceCssGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        family: 'My Font',
        sources: [
            { url: 'fonts/myfont.woff2', format: 'woff2' },
            { url: 'fonts/myfont.woff', format: 'woff' },
        ],
        weight: '400',
        style: 'normal',
        display: 'swap',
        unicodeRange: '',
        copied: false,
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private get cssCode(): string {
        const { family, sources, weight, style, display, unicodeRange } = this.state;
        const srcLine = sources.filter(s => s.url.trim())
            .map(s => `       url('${s.url}') format('${s.format}')`)
            .join(',\n');
        const unicodeLine = unicodeRange.trim() ? `  unicode-range: ${unicodeRange};\n` : '';
        return `@font-face {\n  font-family: '${family}';\n  src: ${srcLine.trim()};\n  font-weight: ${weight};\n  font-style: ${style};\n  font-display: ${display};\n${unicodeLine}}`;
    }

    updateSource = (i: number, partial: Partial<FontSource>) => {
        const sources = [...this.state.sources];
        sources[i] = { ...sources[i], ...partial };
        this.setState({ sources });
    };

    addSource = () => this.setState({ sources: [...this.state.sources, { url: '', format: 'truetype' }] });
    removeSource = (i: number) => this.setState({ sources: this.state.sources.filter((_, idx) => idx !== i) });

    copyCss = () => {
        navigator.clipboard.writeText(this.cssCode).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { family, sources, weight, style, display, unicodeRange, copied } = this.state;

        const form = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Font family</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="ff-family">font-family</label>
                        <input id="ff-family" type="text" value={family}
                            onChange={e => this.setState({ family: e.target.value })} />
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Sources</span></div>
                    {sources.map((s, i) => (
                        <div key={i} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                            <input type="text" value={s.url} placeholder="path/to/font.woff2"
                                style={{ flex: 2, padding: '0.5rem 0.7rem', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.85rem', fontFamily: 'ui-monospace, monospace' }}
                                onChange={e => this.updateSource(i, { url: e.target.value })} />
                            <select value={s.format}
                                style={{ flex: 1, padding: '0.5rem 0.5rem', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: '0.85rem' }}
                                onChange={e => this.updateSource(i, { format: e.target.value as Format })}>
                                <option value="woff2">woff2</option>
                                <option value="woff">woff</option>
                                <option value="truetype">truetype</option>
                                <option value="opentype">opentype</option>
                                <option value="embedded-opentype">eot</option>
                                <option value="svg">svg</option>
                            </select>
                            <button className={genStyles.removeBtn} onClick={() => this.removeSource(i)} aria-label="Remove source">×</button>
                        </div>
                    ))}
                    <button className={genStyles.addBtn} onClick={this.addSource}>+ Add source</button>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Descriptors</span></div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="ff-weight">font-weight</label>
                            <input id="ff-weight" type="text" value={weight}
                                onChange={e => this.setState({ weight: e.target.value })} />
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="ff-style">font-style</label>
                            <select id="ff-style" value={style}
                                onChange={e => this.setState({ style: e.target.value })}>
                                <option value="normal">normal</option>
                                <option value="italic">italic</option>
                                <option value="oblique">oblique</option>
                            </select>
                        </div>
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="ff-display">font-display</label>
                        <select id="ff-display" value={display}
                            onChange={e => this.setState({ display: e.target.value })}>
                            <option value="auto">auto</option>
                            <option value="block">block</option>
                            <option value="swap">swap (recommended)</option>
                            <option value="fallback">fallback</option>
                            <option value="optional">optional</option>
                        </select>
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="ff-range">unicode-range (optional)</label>
                        <input id="ff-range" type="text" value={unicodeRange} placeholder="U+0000-00FF"
                            onChange={e => this.setState({ unicodeRange: e.target.value })} />
                    </div>
                </div>
            </>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Generated CSS"
                previewMeta={`${this.cssCode.split('\n').length} lines`}
                preview={<CodeBlock code={this.cssCode} language="css" wrapLines maxHeight={500} minHeight={320} ariaLabel="Generated @font-face CSS" />}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyCss}>
                        {copied ? '✓ Copied' : '📋 Copy CSS'}
                    </button>
                }
            />
        );
    }
}
